import { motion } from "framer-motion"
import { useThemeSurfaces } from "../../theme/useThemeSurfaces"
import type { ContactMessage, GhostNote } from "../../types"
import { formatConfidenceScore } from "../../utils/ghostNotes"
import { Tooltip } from "../Tooltip"

type MessageListProps = {
  messages: ContactMessage[]
  pending?: boolean
  ghostNotes?: Record<number, GhostNote>
  onCreateGhostNote?: (messageId: number) => void
  onEditGhostNote?: (messageId: number) => void
}

export function MessageList({
  messages,
  pending,
  ghostNotes,
  onCreateGhostNote,
  onEditGhostNote,
}: MessageListProps) {
  const mutedColor = useThemeSurfaces().panel.primary.muted

  if (!messages.length) {
    return <div className="small">No messages yet. Compose a note to begin.</div>
  }

  return (
    <div
      style={{
        display: "grid",
        gap: "12px",
        maxHeight: "360px",
        overflowY: "auto",
        paddingRight: "8px",
      }}
    >
      {messages.map((message) => {
        const isOutbound = message.direction === "outbound"
        const status = message.delivery_status ?? (isOutbound ? "pending" : "received")
        const inlineNote = pickGhostNote(message, ghostNotes)
        const isInbound = !isOutbound
        return (
          <div
            key={`${message.id}-${message.created_at}`}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              alignItems: isOutbound ? "flex-end" : "flex-start",
            }}
          >
            <div className="small" style={{ color: mutedColor, textAlign: isOutbound ? "right" : "left" }}>
              {new Date(message.created_at).toLocaleString()} · {isOutbound ? "You" : "Customer"}
            </div>
            <div
              className="mono"
              style={{
                whiteSpace: "pre-wrap",
                textAlign: isOutbound ? "right" : "left",
                border: "1px solid var(--rail)",
                padding: "10px 12px",
              }}
            >
              {message.body}
            </div>
            {isOutbound ? (
              <div className="tiny mono" style={{ color: mutedColor }}>
                {status.toUpperCase()}
              </div>
            ) : null}
            {inlineNote ? (
              <GhostNoteCallout
                note={inlineNote}
                align={isOutbound ? "right" : "left"}
                onEdit={() => onEditGhostNote?.(message.id)}
              />
            ) : isInbound ? (
              <GhostNoteAdorn onClick={() => onCreateGhostNote?.(message.id)} />
            ) : null}
          </div>
        )
      })}
      {pending ? <div className="small">Fetching latest…</div> : null}
    </div>
  )
}

export default MessageList

function pickGhostNote(message: ContactMessage, index?: Record<number, GhostNote>) {
  const mapped = index?.[message.id]
  if (mapped) return mapped
  if (!message.ghost_note) return null
  const raw = message.ghost_note as Record<string, unknown>
  const rawSummary = typeof raw["summary"] === "string" ? (raw["summary"] as string) : null
  const summary = rawSummary ?? "Private note saved"
  return {
    message_id: message.id,
    summary,
    ghost_note: message.ghost_note,
    confidence_label: message.confidence_label ?? null,
    confidence_score: message.confidence_score ?? null,
    confidence_sources: message.confidence_sources ?? [],
    created_at: message.created_at,
    updated_at: message.created_at,
  }
}

function GhostNoteCallout({
  note,
  align,
  onEdit,
}: {
  note: GhostNote
  align: "left" | "right"
  onEdit?: () => void
}) {
  const surfaces = useThemeSurfaces()
  const titleParts = [note.confidence_label?.toUpperCase() ?? "UNKNOWN", formatConfidenceScore(note.confidence_score)]
  const mutedColor = surfaces.panel.primary.muted
  const highlightSoft = surfaces.highlight.soft
  const highlightPill = surfaces.highlight.pill
  const dashedBorder = surfaces.border.dashed
  const sources = Array.isArray(note.confidence_sources) ? note.confidence_sources : []
  if (sources.length) {
    titleParts.push(sources.join(", "))
  }

  return (
    <Tooltip label={titleParts.join(" · ")}>
        <motion.div
          layout
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
          style={{
            alignSelf: align === "right" ? "flex-end" : "flex-start",
            border: dashedBorder,
            padding: "10px 12px",
            background: highlightSoft,
            display: "grid",
            gap: "6px",
            maxWidth: "min(320px, 80%)",
          }}
        >
        <div className="tiny mono" style={{ color: mutedColor }}>
          Private note · {note.confidence_label ? note.confidence_label.toUpperCase() : "UNKNOWN"}
        </div>
      <div className="mono" style={{ fontSize: "12px", lineHeight: 1.4 }}>{note.summary}</div>
      {sources.length ? (
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {sources.map((source) => (
              <span
                key={source}
                className="tiny mono"
                style={{
                  background: highlightPill,
                  padding: "2px 6px",
                  letterSpacing: "0.08em",
                }}
              >

              {source}
            </span>
          ))}
        </div>
      ) : null}
        {onEdit ? (
          <button
            type="button"
            className="tiny mono"
            onClick={onEdit}
            style={{
              justifySelf: align === "right" ? "flex-end" : "flex-start",
              color: mutedColor,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
          Edit note
        </button>
      ) : null}
      </motion.div>
    </Tooltip>
  )
}

function GhostNoteAdorn({ onClick }: { onClick?: () => void }) {
  const surfaces = useThemeSurfaces()
  const mutedColor = surfaces.panel.primary.muted
  if (!onClick) return null

  return (
    <button
      type="button"
      onClick={onClick}
      className="tiny mono"
      style={{
        alignSelf: "flex-start",
        background: "transparent",
        border: surfaces.border.dashed,
        padding: "6px 8px",
        letterSpacing: "0.08em",
        color: mutedColor,
        cursor: "pointer",
      }}
    >
      + Add note
    </button>
  )
}
